<template>
  <v-container class="test-requirements">
    <!-- Header with action button -->
    <div class="action-buttons">
      <v-btn color="primary" @click="openCreateModal">
        <v-icon left>mdi-plus-circle</v-icon>
        Add Requirement
      </v-btn>
    </div>

    <!-- Empty State -->
    <div v-if="requirements.length === 0" class="empty-state">
      <v-icon size="80" color="primary">mdi-database-search-outline</v-icon>
      <h3>No Requirements</h3>
      <p>Add requirements to define data and simulation needs</p>
    </div>

    <!-- Requirements List -->
    <div v-else class="requirements-list-container">
      <RequirementCard
        v-for="requirement in requirements"
        :key="requirement.id"
        :requirement="requirement"
        @edit="openEditModal"
        @delete="confirmDeleteRequirement"
      />
    </div>

    <!-- Requirement Edit Modal -->
    <RequirementEditModal
      :value="showEditModal"
      :requirement="requirementToEdit"
      :attributes="attributes"
      :selected-problem="selectedProblem"
      :is-saving="isSaving"
      @close="showEditModal = $event"
      @save="saveRequirement"
      @delete="confirmDeleteRequirement"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card class="confirm-dialog">
        <v-card-title class="confirm-dialog__title">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>
        <v-card-text class="confirm-dialog__content">
          <p>Are you sure you want to delete this requirement?</p>
          <p class="warning-text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="confirm-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteRequirement">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      @click="snackbar = false"
      style="cursor: pointer"
    >
      <v-icon left v-if="snackbarColor === 'success'">mdi-check-circle</v-icon>
      <v-icon left v-else-if="snackbarColor === 'error'"
        >mdi-alert-circle</v-icon
      >
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import RequirementCard from "./RequirementCard.vue";
import RequirementEditModal from "./RequirementEditModal.vue";
import apiClient from "@/plugins/apiService";

export default {
  name: "TestRequirements",

  components: {
    RequirementCard,
    RequirementEditModal,
  },

  props: {
    selectedProblem: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      requirements: [],
      attributes: [],
      requirementToEdit: null,
      requirementToDelete: null,

      showEditModal: false,
      confirmDialog: false,
      isSaving: false,

      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },

  watch: {
    selectedProblem() {
      this.fetchRequirements();
    },
  },

  mounted() {
    this.fetchRequirements();
    this.fetchAttributes();
  },

  methods: {
    // ==================== Modal Controls ====================
    openCreateModal() {
      this.requirementToEdit = null;
      this.showEditModal = true;
    },

    openEditModal(requirement) {
      this.requirementToEdit = { ...requirement };
      this.showEditModal = true;
    },

    // ==================== API Methods ====================
    async fetchRequirements() {
      try {
        const response = await apiClient.get(`/test/${this.selectedProblem}`);
        const data = response.data;
        this.requirements = data;
      } catch (error) {
        console.error("Error fetching requirements:", error);
        this.showErrorMessage("Failed to load requirements");
        this.requirements = [];
      }
    },

    async fetchAttributes() {
      try {
        const response = await apiClient.get(`/attribute`);
        const data = response.data;
        this.attributes = data;
      } catch (error) {
        console.error("Error fetching attributes:", error);
        this.showErrorMessage("Failed to load attributes");
        this.attributes = [];
      }
    },

    async saveRequirement(requirementData) {
      this.isSaving = true;

      try {
        const isUpdate = requirementData.id !== undefined;
        const url = isUpdate ? `/test/${requirementData.id}` : `/test`;

        const response = isUpdate
          ? await apiClient.put(url, requirementData)
          : await apiClient.post(url, requirementData);

        if (response.status === 200 || response.status === 201) {
          await this.fetchRequirements();
          this.showEditModal = false;
          this.showSuccessMessage(
            isUpdate ? "Requirement updated" : "Requirement created"
          );
        } else {
          throw new Error(response.statusText);
        }
      } catch (error) {
        console.error("Error saving requirement:", error);
        this.showErrorMessage("Failed to save requirement");
      } finally {
        this.isSaving = false;
      }
    },

    // ==================== Delete Operations ====================
    confirmDeleteRequirement(requirementId) {
      this.requirementToDelete = requirementId;
      this.confirmDialog = true;
      this.showEditModal = false;
    },

    async deleteRequirement() {
      if (!this.requirementToDelete) return;

      try {
        const response = await apiClient.delete(
          `/test/${this.requirementToDelete}`
        );

        if (response.status === 200) {
          await this.fetchRequirements();
          this.confirmDialog = false;
          this.showSuccessMessage("Requirement deleted");
        } else {
          throw new Error(response.statusText);
        }
      } catch (error) {
        console.error("Error deleting requirement:", error);
        this.showErrorMessage("Failed to delete requirement");
      } finally {
        this.requirementToDelete = null;
      }
    },

    // ==================== Notifications ====================
    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.requirements-list-container {
  max-height: 400px;
  overflow-y: auto;
}

/* Confirm Dialog */
.confirm-dialog__title {
  background-color: var(--primaryColor) !important;
  color: var(--colorOnPrimary) !important;
}
</style>
